import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Download, Link2Off } from 'lucide-react';
import PhotoDetail from '../components/PhotoDetail';
import { GRID, PhotoCard, PhotoGridSkeleton } from '../components/admin/PhotoGrid';
import { Button, cx, EmptyState, FOCUS, Notice, PageHeader, StatRow } from '../components/admin/ui';
import { BASE_URL, formatBytes, formatDate } from '../lib/admin';

const PAGE = 60;
const number = (n) => (n || 0).toLocaleString('es-PE');

// Public page of a dataset shared by an administrator (no login needed)
export default function SharedDataset() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [photos, setPhotos] = useState([]);
  const [error, setError] = useState('');
  const [loadingMore, setLoadingMore] = useState(false);
  const [selected, setSelected] = useState(null);
  const base = `${BASE_URL}/shares/${encodeURIComponent(token)}/`;

  useEffect(() => {
    fetch(`${base}?limit=${PAGE}`)
      .then(async (response) => {
        const body = await response.json().catch(() => ({}));
        if (!response.ok) throw new Error(body.error || `Error ${response.status}`);
        setData(body);
        setPhotos(body.photos);
      })
      .catch((e) => setError(e.message));
  }, [base]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const response = await fetch(`${base}?limit=${PAGE}&offset=${photos.length}`);
      const body = await response.json();
      if (!response.ok) throw new Error(body.error || `Error ${response.status}`);
      setPhotos((current) => [...current, ...body.photos]);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoadingMore(false);
    }
  };

  const index = selected === null ? -1 : selected;
  const s = data?.summary;

  return (
    <div className="min-h-screen bg-paper font-ui text-ink">
      <div className="mx-auto max-w-7xl space-y-8 px-4 pb-24 pt-10 md:px-10 md:pt-14">
        {error && !data ? (
          <div className="pt-10">
            <EmptyState icon={Link2Off} title={error}>Pide un nuevo enlace a quien te compartió este dataset.</EmptyState>
          </div>
        ) : (
          <>
            <PageHeader
              eyebrow="Dataset compartido · Chillaos Project"
              title={data ? data.name : '…'}
              description={data && `Fotografías de documentos de archivo tomadas en campo, con su ubicación y metadata. Disponible hasta el ${formatDate(data.expires_at)}.`}
              actions={data && (
                <a href={`${base}?format=csv`}
                  className={cx('inline-flex h-11 items-center gap-2 rounded-lg border border-ink bg-ink px-5 text-[15px] font-medium text-parchment transition-colors hover:bg-umber', FOCUS)}>
                  <Download className="h-4 w-4" strokeWidth={1.75} /> Descargar CSV
                </a>
              )}
            />
            {s && (
              <StatRow items={[
                { label: 'Fotos', value: number(s.photos) },
                { label: 'Contribuyentes', value: number(s.contributors) },
                { label: 'Regiones', value: s.regions },
                { label: 'Con GPS', value: `${s.photos ? Math.round((s.with_gps / s.photos) * 100) : 0}%`, sub: `${number(s.with_gps)} fotos` },
                { label: 'Volumen', value: formatBytes(s.bytes) },
              ]} />
            )}
            {data && <p className="font-mono text-[10.5px] uppercase tracking-[0.12em] text-umber/45">El CSV incluye la metadata de cada foto y enlaces a la imagen original válidos por 7 días.</p>}
            {error && <Notice onClose={() => setError('')}>{error}</Notice>}

            {!data ? <PhotoGridSkeleton /> : (
              <div className={GRID}>
                {photos.map((p, i) => (
                  <PhotoCard
                    key={p.id}
                    photo={p}
                    onClick={() => setSelected(i)}
                    title={p.folder || 'Sin nombre'}
                    subtitle={`${p.region || '—'} · ${formatDate(p.taken_at)}`}
                  />
                ))}
              </div>
            )}
            {data && photos.length < data.total && (
              <div className="flex justify-center">
                <Button loading={loadingMore} onClick={loadMore}>Cargar {Math.min(PAGE, data.total - photos.length)} más</Button>
              </div>
            )}
          </>
        )}
      </div>

      {index >= 0 && photos[index] && (
        <PhotoDetail
          photo={photos[index]}
          position={`${index + 1} / ${data.total}`}
          contributorLabel="Contribuyente"
          onClose={() => setSelected(null)}
          onPrev={index > 0 ? () => setSelected(index - 1) : null}
          onNext={index < photos.length - 1 ? () => setSelected(index + 1) : null}
        />
      )}
    </div>
  );
}
